import React from 'react';
import { Interpolation, Theme } from '@emotion/react';
import SvgSafeGatsbyImage from '../atoms/SvgSafeGatsbyImage';
import Container from '../primitives/grid/container';
import apCaseOnlyTitleTags from '../utils/apCaseOnlyTitleTags';
import RichTextEditorStyles from '../utils/RichTextEditorStyles';
import { colors } from '../primitives/colors';
import Col from '../primitives/grid/col';
import Row from '../primitives/grid/row';
import Link from '../primitives/link';
import NotchedSlider from '../primitives/notched-slider';
import { StyledButtonAsLink } from '../primitives/styled-button';
import { breakpoints } from '../primitives/tokens';
import Typography from '../primitives/typography';
import Divider from '../primitives/divider';
import Pagination from '../primitives/notched-slider/pagination';

interface Slide {
    copy: string;
    heading?: string;
    icon?: {
        url: string;
        title: string;
    };
    link?: {
        title?: string;
        href?: string;
    };
    link_as_text?: string;
    id?: string;
}

interface IconBlurbsSliderData {
    heading?: string;
    sub_headline?: string;
    settings: {
        dark_mode?: boolean;
        icon_on_left?: boolean;
        number_of_slides?: number;
    };
    slides: Slide[];
    spacing?: string;
}

const NullableLink = ({ link, linkAsText }: { link?: { title?: string, href?: string }, linkAsText?: string }): JSX.Element | null => {
    if (!link || !link.title) {
        return null;
    }

    let innerJsx;

    if (linkAsText && link?.href) {
        innerJsx = (
            <Link
                className="hover-default"
                css={{
                    color: colors.primaryOrange,
                }}
                to={link.href}
            >
                {link.title}
            </Link>
        );
    } else if (link?.href) {
        innerJsx = (
            <StyledButtonAsLink to={link.href}>
                {link.title}
            </StyledButtonAsLink>
        );
    }

    return (
        <div css={{
            display: 'flex',
            marginTop: '0.75rem',
            justifyContent: 'flex-start',
        }}
        >
            {innerJsx}
        </div>
    );
};

const IconBlurbsSlider = ({ data }: { data: IconBlurbsSliderData }): JSX.Element => {
    const { heading, sub_headline, settings, slides } = data;
    const { dark_mode, icon_on_left, number_of_slides } = settings;

    const slidesPerViewDesktop = number_of_slides || 3;

    return (
        <NotchedSlider>
            <section css={[{
                padding: '48px 0 0',
                overflow: 'hidden',
                background: dark_mode ? colors.shadesBlack : undefined,
            }]}
            >
                <Container css={{
                    position: 'relative',
                    paddingTop: '0.25rem',
                }}
                >
                    {(heading || sub_headline) && (
                        <div css={{ marginBottom: '2rem' }}>
                            <Typography
                                as="h2"
                                fontWeight="regular"
                                reducedLetterSpacing
                                fontSize="3xl"
                                css={[{
                                    color: dark_mode ? colors.primaryOrange : colors.shadesBlack,
                                    [breakpoints.tb]: {
                                        textAlign: 'center',
                                        maxWidth: '80%',
                                        margin: 'auto',
                                    },
                                }] as Interpolation<Theme>}
                            >
                                {apCaseOnlyTitleTags(heading)}
                            </Typography>
                            {!!sub_headline && (
                                <Typography
                                    fontSize="xl"
                                    css={[RichTextEditorStyles, {
                                        color: dark_mode ? colors.shadesWhite : undefined,
                                        marginTop: '0.25rem',
                                        lineHeight: '1.75rem',
                                    }] as Interpolation<Theme>}
                                    dangerouslySetInnerHTML={{
                                        __html: apCaseOnlyTitleTags(sub_headline),
                                    }}
                                />
                            )}
                        </div>
                    )}
                </Container>
                <Divider css={{
                    opacity: dark_mode ? 0.1 : 0.6,
                }}
                />
                <Container css={{
                    position: 'relative',
                    padding: '48px 0',
                    [breakpoints.tb]: {
                        display: 'none',
                    },
                }}
                >
                    <div css={{
                        position: 'absolute',
                        height: '100%',
                        width: `calc(50vw - ${1440 / 2}px - 32px)`,
                        background: `linear-gradient(90deg, transparent, ${dark_mode ? colors.shadesBlack : colors.shadesWhite})`,
                        right: '0px',
                        top: '0px',
                        transform: 'translateX(100%)',
                        zIndex: 2,
                        pointerEvents: 'none',
                    }}
                    />
                    <div css={{
                        position: 'absolute',
                        height: '100%',
                        width: `calc(50vw - ${1440 / 2}px - 32px)`,
                        background: `linear-gradient(-90deg, transparent, ${dark_mode ? colors.shadesBlack : colors.shadesWhite})`,
                        left: '0px',
                        top: '0px',
                        transform: 'translateX(-100%)',
                        zIndex: 2,
                        pointerEvents: 'none',
                    }}
                    />
                    <NotchedSlider.Slides overflowVisible centered masksLeft slidesPerView={slidesPerViewDesktop}>
                        {slides.map((slide: Slide, slideIndex: number) => {
                            const {
                                copy,
                                heading: slideHeading,
                                icon,
                                link,
                                link_as_text,
                            } = slide;
                            return (
                                <Col
                                    key={slide.id || `desktop-slide-${slideIndex + 1}`}
                                    breakpoints={{ dt: { span: 6 }, tb: { span: 3 }, mb: { span: 2 } }}
                                    css={{
                                        position: 'relative',
                                        paddingTop: slideHeading ? '24px' : '0',
                                        paddingBottom: slideHeading ? '24px' : '0',
                                        paddingRight: '48px',
                                        height: '100%',
                                        '&:after': {
                                            right: '24px',
                                            content: '""',
                                            position: 'absolute',
                                            top: '0',
                                            height: '100%',
                                            backgroundColor: colors.grey200,
                                            opacity: dark_mode ? 0.2 : 1,
                                            width: '1px',
                                        },
                                        '&:nth-of-type(4n):after': {
                                            display: 'none',
                                        },
                                        [breakpoints.mb]: {
                                            '&:after': {
                                                right: '-8px',
                                            },
                                            '&:nth-of-type(2n):after': {
                                                display: 'none',
                                            },
                                        },
                                    }}
                                >
                                    <div css={{
                                        color: dark_mode ? colors.shadesWhite : undefined,
                                        display: 'flex',
                                        gap: '10px',
                                        flexDirection: (icon_on_left) ? 'row' : 'column',
                                        [breakpoints.tb]: {
                                            flexDirection: 'column',
                                        },
                                    } as Interpolation<Theme>}
                                    >
                                        {!!icon?.url && (
                                            <div css={{
                                                maxWidth: '40px',
                                                minWidth: '40px',
                                                marginBottom: icon_on_left ? '0.5rem' : '0.25rem',
                                                marginLeft: icon_on_left ? '0' : '-7px',
                                                marginRight: '16px',
                                                marginTop: '12px',
                                            }}
                                            >
                                                <SvgSafeGatsbyImage image={icon} alt={icon.title} />
                                            </div>
                                        )}
                                        <div
                                            css={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                paddingTop: '0.25rem',
                                            }}
                                        >
                                            {slideHeading && (
                                                <Typography
                                                    fontSize="xl"
                                                    as="h3"
                                                    css={{
                                                        marginBottom: '0.5rem',
                                                        fontWeight: 600,
                                                        color: dark_mode ? colors.shadesWhite : undefined,
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: slideHeading,
                                                    }}
                                                />
                                            )}
                                            <div
                                                css={RichTextEditorStyles}
                                                // eslint-disable-next-line react/no-danger
                                                dangerouslySetInnerHTML={{
                                                    __html: copy || '',
                                                }}
                                            />
                                            <NullableLink link={link} linkAsText={link_as_text} />
                                        </div>
                                    </div>
                                </Col>
                            );
                        })}
                    </NotchedSlider.Slides>
                </Container>
                <Container css={{
                    position: 'relative',
                    display: 'none',
                    padding: '18px 0',
                    minHeight: 'calc(100% + 32px)',
                    [breakpoints.tb]: {
                        display: 'block',
                    },
                }}
                >
                    <NotchedSlider.Slides overflowVisible centered masksLeft masksRight slidesPerView={1}>
                        {slides.map((slide: Slide, slideIndex: number) => {
                            const {
                                copy,
                                heading: slideHeading,
                                icon,
                                link,
                                link_as_text,
                            } = slide;
                            return (
                                <Col
                                    key={slide.id || `mobile-slide-${slideIndex + 1}`}
                                    breakpoints={{ dt: { span: 6 }, tb: { span: 3 }, mb: { span: 2 } }}
                                    css={{
                                        position: 'relative',
                                        paddingTop: slideHeading ? '24px' : '0',
                                        paddingBottom: slideHeading ? '24px' : '0',
                                        height: '100%',
                                        minHeight: 'calc(100% + 32px)',
                                    }}
                                >
                                    <div css={{
                                        color: dark_mode ? colors.shadesWhite : undefined,
                                        display: 'flex',
                                        gap: '16px',
                                        flexDirection: (icon_on_left) ? 'row' : 'column',
                                        [breakpoints.tb]: {
                                            flexDirection: 'column',
                                        },
                                    } as Interpolation<Theme>}
                                    >
                                        {!!icon?.url && (
                                            <div css={{
                                                maxWidth: '50px',
                                                minWidth: '50px',
                                                paddingBottom: icon_on_left ? '0.5rem' : '0.25rem',
                                                paddingRight: '16px',
                                                paddingTop: '12px',
                                            }}
                                            >
                                                <SvgSafeGatsbyImage image={icon} alt={icon.title} />
                                            </div>
                                        )}
                                        <div
                                            css={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                paddingTop: '0.25rem',
                                            }}
                                        >
                                            {slideHeading && (
                                                <Typography
                                                    fontSize="xl"
                                                    as="h3"
                                                    css={{
                                                        paddingBottom: '0.5rem',
                                                        fontWeight: 600,
                                                        color: dark_mode ? colors.shadesWhite : undefined,
                                                    }}
                                                    // eslint-disable-next-line react/no-danger
                                                    dangerouslySetInnerHTML={{
                                                        __html: slideHeading || '',
                                                    }}
                                                />
                                            )}
                                            <div
                                                css={RichTextEditorStyles}
                                                // eslint-disable-next-line react/no-danger
                                                dangerouslySetInnerHTML={{
                                                    __html: copy || '',
                                                }}
                                            />
                                            <NullableLink link={link} linkAsText={link_as_text} />
                                        </div>
                                    </div>
                                </Col>
                            );
                        })}
                    </NotchedSlider.Slides>
                </Container>
                <Divider css={{
                    opacity: dark_mode ? 0.1 : 0.6,
                }}
                />
                <Container css={{
                    position: 'relative',
                    [breakpoints.tb]: {
                        display: 'none',
                    },
                }}
                >
                    <Row>
                        <Col breakpoints={{
                            dt: { start: 4, span: 4 },
                            mb: { start: 0, span: 4 },
                        }}
                        >
                            <Pagination slidesLength={slides.length} />
                        </Col>
                    </Row>
                </Container>
                <Container css={{
                    position: 'relative',
                    display: 'none',
                    [breakpoints.tb]: {
                        display: 'block',
                    },
                }}
                >
                    <Row>
                        <Col breakpoints={{
                            dt: { start: 4, span: 4 },
                            mb: { start: 0, span: 4 },
                        }}
                        >
                            <Pagination slidesLength={slides.length} />
                        </Col>
                    </Row>
                </Container>
            </section>
        </NotchedSlider>
    );
};

export default IconBlurbsSlider;
